// @ts-nocheck
import { capitalize, isEmpty } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { ButtonUI, TooltipButton, useDidUpdate } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { getMetricFormatFromOverviewItem } from '@/helpers/reporting.helpers';
import { reportingDownloadSummaryData } from '@/store/actions/reporting.actions';
import { getDownloadSummaryData, getSelectedCampaigns } from '@/store/selectors/reporting.selectors';
import { getCampaigns } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { useSummaryContext } from '@/ts-contexts/reporting/Summary/SummaryContext';

import './summaryDownload.scss';

import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';

export function SummaryDownload(): React.ReactElement | null {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const { getSummaryDOOHStats, getSummaryItems, getSummaryStats, summaryItemsKeys } = useSummaryContext();
  const stats = {
    ...getSummaryStats(),
    ...getSummaryDOOHStats()
  };
  const campaignIds: string[] = useSelector(getCampaigns);
  const reportingCampaigns = useSelector(getSelectedCampaigns);
  const campaigns: Record<string, any>[] = reportingCampaigns.filter(({ id }: { id: string }) =>
    campaignIds.includes(id)
  );
  const userRoles: UserRoleInterface[] = useSelector(getUserRoles);
  const user: UserInterface = useSelector(getCurrentUser);

  const downloadSummaryData = useSelector(getDownloadSummaryData);
  const summaryItems = getSummaryItems(summaryItemsKeys, campaigns, stats!) || [];
  const downloadSummaryPermissions = checkUserResourcePermissions('reportingDownloadSummaryData', user, userRoles);

  const downloadSummaryDataToken: Record<string, any> = {};

  const [{ downloadSummaryLoading }, setState] = useState({
    downloadSummaryLoading: downloadSummaryData.status !== 'finished'
  });

  useEffect(() => {
    return () => {
      downloadSummaryDataToken && downloadSummaryDataToken.cancel && downloadSummaryDataToken.cancel();
    };
  }, []);

  useDidUpdate(() => {
    setState(prevState => ({
      ...prevState,
      downloadSummaryLoading: downloadSummaryData.status !== 'finished'
    }));
    if (downloadSummaryData.status === 'finished' && !isEmpty(downloadSummaryData.result)) {
      window.open(downloadSummaryData.result.urlFile);
    }
  }, [downloadSummaryData]);

  const downloadSummary = useCallback(
    (fileFormat = 'excel') => {
      const columns: {
        cpy: string;
        extra?: boolean;
        format?: string;
        prop: string;
      }[] = ['campaignId', 'cid'].map(campaignColumn => ({
        cpy: formatMessage({
          defaultMessage: capitalize(campaignColumn),
          id: `reporting.summary.downloadSummary.${campaignColumn}`
        }),
        extra: true,
        prop: campaignColumn
      }));
      const extraColumns: {
        cpy: string;
        extra?: boolean;
        format?: string;
        prop: string;
      }[] = [];
      summaryItems
        .filter(({ dooh }: { dooh: boolean }) => !dooh)
        .forEach(({ main, secondary }) => {
          const mainColumn = {
            cpy: formatMessage({
              defaultMessage: capitalize(main.metricKey),
              id: main.name
            }),
            format: getMetricFormatFromOverviewItem(main),
            prop: main.metricKey
          };
          const secondaryColumns = (secondary || []).map(secondaryMetric => ({
            cpy: formatMessage({
              defaultMessage: capitalize(secondaryMetric.metricKey),
              id: secondaryMetric.name
            }),
            format: getMetricFormatFromOverviewItem(secondaryMetric),
            prop: secondaryMetric.metricKey
          }));
          extraColumns.push(mainColumn, ...secondaryColumns);
        });
      columns.push(...extraColumns);
      const sheetHeadersTitle = formatMessage({
        defaultMessage: 'Campaign line details',
        id: 'reporting.summary.downloadSummary.title'
      });
      dispatch(reportingDownloadSummaryData(fileFormat, columns, sheetHeadersTitle, downloadSummaryDataToken));
    },
    [summaryItems, downloadSummaryDataToken]
  );

  if (!downloadSummaryPermissions) {
    return null;
  }

  return (
    <div className="reportingOverview__downloadContainer">
      <TooltipButton
        addListener
        buttonConfig={{
          buttonLoading: downloadSummaryLoading
        }}
        className="reportingOverview__downloadButton"
        icon="fas fa-arrow-to-bottom"
        id="download-summary"
        isButton
        tooltipConfig={{
          tooltipEvent: 'click',
          tooltipPlace: 'left'
        }}
      >
        <div className="reportingOverview__downloadButtons">
          {['excel', 'csv'].map(downloadButtonType => (
            <ButtonUI
              className={`reportingOverview__downloadButtons__${downloadButtonType}Button`}
              icon="fas fa-arrow-down"
              id={`download-summary-${downloadButtonType}`}
              key={`download-summary-${downloadButtonType}`}
              onClick={() => downloadSummary(downloadButtonType)}
              shape="link"
              size="small"
              text={formatMessage({
                defaultMessage: capitalize(downloadButtonType),
                id: `reporting.summary.downloadSummary.${downloadButtonType}`
              })}
              type="secondary"
            />
          ))}
        </div>
      </TooltipButton>
    </div>
  );
}
